<template>
  <div>
    <Header />
    
    <!-- Schöner Loading-Overlay -->
    <v-overlay :value="loading" :opacity="0.9" z-index="999">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="80"
          width="5"
          color="primary"
          class="mb-4"
        ></v-progress-circular>
        <h3 class="white--text font-weight-medium">Daten werden geladen...</h3>
        <p class="white--text mt-2 subtitle-1">Bitte warten Sie einen Moment</p>
      </div>
    </v-overlay>
    
    <v-container fluid>
      <v-card :loading="loading" elevation="2" class="mb-6">
        <v-toolbar flat color="primary" dark>
          <v-toolbar-title>{{ app.name || 'App Dashboard' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn small outlined class="mr-2" @click="edit">
            <v-icon small left>mdi-pencil</v-icon>
            Bearbeiten
          </v-btn>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Suche"
            single-line
            hide-details
            filled
            dense
            rounded
            class="mx-2 mt-1"
            @keyup.enter="doSearch()"
          ></v-text-field>
          <v-btn icon @click="refresh">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-container>
            <v-row align="center" justify="center" class="mb-4">
              <v-col cols="12" md="4" xl="2">
                <v-hover v-slot:default="{ hover }">
                  <v-card
                    class="mx-auto"
                    :elevation="hover ? 12 : 2"
                    @click="open('customers')"
                  >
                    <v-img
                      src="/img/Card_Customer.svg"
                      class="white--text"
                      width="100%"
                      cover
                      gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
                    >
                      <v-card-title
                        class="align-end fill-height pa-0 ma-0"
                        style="font-size: 1.4em; word-wrap: break-word"
                      >
                        <v-container>
                          <v-row no-gutters>Kunden</v-row>
                        </v-container>
                      </v-card-title>
                    </v-img>

                    <v-card-text>
                      <v-container>
                        <v-row no-gutters>
                          Verwaltung von App Teilnehmern.
                        </v-row>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-hover>
              </v-col>
              <v-col cols="12" md="4" xl="2">
                <v-hover v-slot:default="{ hover }">
                  <v-card
                    class="mx-auto"
                    :elevation="hover ? 12 : 2"
                    @click="open('products')"
                  >
                    <v-img
                      src="/img/Card_Products.svg"
                      class="white--text"
                      width="100%"
                      cover
                      gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
                    >
                      <v-card-title
                        class="align-end fill-height pa-0 ma-0"
                        style="font-size: 1.4em; word-wrap: break-word"
                      >
                        <v-container>
                          <v-row no-gutters>Produkte</v-row>
                        </v-container>
                      </v-card-title>
                    </v-img>

                    <v-card-text>
                      <v-container>
                        <v-row no-gutters>Verwaltung der Produkte.</v-row>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-hover>
              </v-col>
              <v-col cols="12" md="4" xl="2">
                <v-hover v-slot:default="{ hover }">
                  <v-card
                    class="mx-auto"
                    :elevation="hover ? 12 : 2"
                    @click="open('customs')"
                  >
                    <v-img
                      src="/img/Card_User.svg"
                      class="white--text"
                      width="100%"
                      cover
                      gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
                    >
                      <v-card-title
                        class="align-end fill-height pa-0 ma-0"
                        style="font-size: 1.4em; word-wrap: break-word"
                      >
                        <v-container>
                          <v-row no-gutters>Zoll Dokumente</v-row>
                        </v-container>
                      </v-card-title>
                    </v-img>

                    <v-card-text>
                      <v-container>
                        <v-row no-gutters>Drucken von Zolldokumenten</v-row>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-hover>
              </v-col>
            </v-row>

            <!-- Stündliche Bestellungsstatistik als Balkendiagramm -->
            <v-row>
              <v-col cols="12">
                <v-card class="mb-6" elevation="2">
                  <v-card-title class="text-h5 font-weight-bold">
                    Bestellungen pro Stunde
                    <v-spacer></v-spacer>
                    <v-menu
                      v-model="dateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="selectedDateFormatted"
                          label="Datum wählen"
                          prepend-icon="mdi-calendar"
                          readonly
                          dense
                          outlined
                          class="mt-0 pt-0 mx-2"
                          style="max-width: 180px;"
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="selectedDate"
                        @input="dateMenu = false"
                        locale="de"
                        :max="new Date().toISOString().substr(0, 10)"
                      ></v-date-picker>
                    </v-menu>
                    <v-btn small outlined color="primary" @click="refreshHourlyStats">
                      <v-icon small left>mdi-refresh</v-icon>
                      Aktualisieren
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <div style="position: relative; height: 250px;">
                      <OrdersHourlyChart :chart-data="hourlyOrdersData" :options="chartOptions"/>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <div class="d-flex align-center mb-4">
                  <h2 class="text-h5 font-weight-bold">Bestellungen</h2>
                  <v-spacer></v-spacer>
                  <v-btn-toggle v-model="filterMode" mandatory class="mr-3">
                    <v-btn small value="all" color="primary" class="px-2">
                      Alle
                    </v-btn>
                    <v-btn small value="errors" color="error" class="px-2">
                      Fehlerhafte
                    </v-btn>
                  </v-btn-toggle>
                  <v-chip small class="mr-2" color="amber darken-2" text-color="white">Fehlerhafte: {{ getOrderCountWithErrors() }}</v-chip>
                  <v-chip small class="mr-2" color="green" text-color="white">Versandt: {{ getOrderCountByStatus('shipped') }}</v-chip>
                  <v-chip small class="mr-2" color="orange" text-color="white">Ausstehend: {{ getOrderCountByStatus('pending') }}</v-chip>
                  <v-chip small class="mr-2" color="red" text-color="white">Storniert: {{ getOrderCountByStatus('cancled') }}</v-chip>
                </div>

                <v-data-table
                  :v-if="orders"
                  :headers="orderHeaders"
                  :items="filteredOrders"
                  :sort-by="['updated', 'status']"
                  :sort-desc="true"
                  :search="search"
                  show-expand
                  items-per-page="100"
                  class="elevation-1"
                  :footer-props="{
                    'items-per-page-options': [25, 50, 100, -1],
                    'items-per-page-text': 'Einträge pro Seite'
                  }"
                  :loading-text="'Daten werden geladen...'"
                  :no-data-text="'Keine Daten verfügbar'"
                  :no-results-text="'Keine Ergebnisse gefunden'"
                >
                  <template v-slot:item.date="{ item }">
                    <span>{{ item.date | moment("DD.MM.YYYY HH:mm") }}</span>
                  </template>
                  
                  <template v-slot:item.updated="{ item }">
                    <span>{{ item.updated | moment("DD.MM.YYYY HH:mm") }}</span>
                  </template>

                  <template v-slot:item.name1="{ item }">
                    <div class="font-weight-medium">{{ item.name1 }} {{ item.name2 }}</div>
                  </template>

                  <template v-slot:item.status="{ item }">
                    <v-chip 
                      small
                      :color="getStatusColor(item.status)" 
                      text-color="white"
                      label
                    >
                      {{ getStatusText(item.status) }}
                    </v-chip>
                  </template>

                  <template v-slot:item.hasError="{ item }">
                    <v-chip
                      x-small
                      :color="hasOrderItemErrors(item) ? 'error' : 'success'"
                      text-color="white"
                    >
                      {{ hasOrderItemErrors(item) ? 'Ja' : 'Nein' }}
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-btn 
                      small 
                      color="error" 
                      text 
                      @click="reportOrder(item)"
                      :disabled="isReporting[item.orderId]"
                      :loading="isReporting[item.orderId]"
                    >
                      <v-icon small left>mdi-flag</v-icon>
                      Melden
                    </v-btn>
                  </template>

                  <template v-slot:item.invoice="{ item }">
                    <v-btn
                      v-if="item.invoice"
                      small
                      color="primary"
                      @click="openLink(item.invoice)"
                      class="text-none"
                    >
                      <v-icon small left>mdi-download</v-icon>
                      Download
                    </v-btn>
                  </template>

                  <template v-slot:item.tracking="{ item }">
                    <a
                      v-if="item.tracking && item.carrier == 'DHL'"
                      target="_blank"
                      class="text-decoration-none"
                      :href="'https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode=' + item.tracking"
                    >
                      <v-chip small outlined color="blue" class="pa-1">
                        <v-icon small left>mdi-truck-delivery</v-icon>
                        {{ item.tracking }}
                      </v-chip>
                    </a>
                    <span v-else-if="item.tracking && item.carrier != 'DHL'">
                      {{ item.tracking }}
                    </span>
                    <v-btn
                      v-else-if="!item.tracking && item.status == 'shipped'"
                      small
                      outlined
                      color="primary"
                      @click="refreshTracking(item.plentyId)"
                    >
                      <v-icon small left>mdi-refresh</v-icon>
                      Beantragen
                    </v-btn>
                    <span v-else class="text-caption">Keine Tracking-Nr.</span>
                  </template>
                  
                  <template v-slot:item.trackingStatus="{ item }">
                    <v-btn
                      v-if="!item.trackingStatus && item.carrier == 'DHL'"
                      small
                      outlined
                      color="primary"
                      @click="trackingStatus(item)"
                    >
                      <v-icon small left>mdi-refresh</v-icon>
                      Abrufen
                    </v-btn>
                    <v-chip
                      small
                      v-if="item.trackingStatus"
                      :color="item.trackingStatus == 'delivered' ? 'success' : 'primary'"
                    >
                      {{ item.trackingStatus }}
                    </v-chip>
                  </template>

                  <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length">
                      <div v-if="item.orderItems && item.orderItems.length > 0">
                        <v-alert 
                          v-if="hasOrderItemErrors(item)" 
                          dense 
                          type="warning" 
                          class="mb-3"
                        >
                          Diese Bestellung enthält keine Artikel mit Design-Daten (line1)
                        </v-alert>
                        
                        <v-card flat v-for="oi of item.orderItems" v-bind:key="oi.id" class="pa-3 ma-2" :class="{'error-border': !hasOrderItemLineData(oi)}">
                          <v-card-title class="d-flex align-center subtitle-1 font-weight-bold py-1">
                            <span>{{ oi.productName }}</span>
                            <v-chip v-if="!hasOrderItemLineData(oi)" x-small color="error" class="ml-2">Fehlende Daten</v-chip>
                          </v-card-title>
                          
                          <v-card-subtitle class="py-1">
                            <div class="d-flex flex-wrap">
                              <span class="mr-3">SKU: <strong>{{ oi.sku }}</strong></span>
                              <span class="mr-3">ID: <strong>{{ oi.id }}</strong></span>
                              <span>Menge: <strong>{{ oi.vQty }}</strong></span>
                              <span v-if="oi.motiv1" class="ml-3">Motiv: <strong>{{ oi.motiv1 }}</strong></span>
                            </div>
                          </v-card-subtitle>
                          
                          <v-card-text v-if="hasOrderItemLineData(oi)">
                            <v-row v-for="i in getVisibleLines(oi)" :key="i">
                              <v-col cols="2">Zeile {{ i }}:</v-col>
                              <v-col cols="10">
                                <img 
                                  v-if="oi['line' + i] && oi['line' + i].startsWith('https')" 
                                  :src="oi['line' + i]" 
                                  alt="Image" 
                                  class="max-height-150" 
                                />
                                <strong v-else>{{ oi["line" + i] }}</strong>
                              </v-col>
                            </v-row>
                          </v-card-text>
                          
                          <v-card-text v-else>
                            <v-alert dense type="error" outlined>
                              Fehler: Keine Design-Daten für dieses Produkt gefunden.
                              <div class="mt-2">
                                <v-btn small color="primary" @click="reportOrder(item)" class="mr-2">
                                  <v-icon small left>mdi-flag</v-icon>
                                  Bestellung melden
                                </v-btn>
                              </div>
                            </v-alert>
                          </v-card-text>
                        </v-card>
                      </div>
                      <v-alert v-else type="info" dense>Keine Bestellpositionen gefunden</v-alert>
                    </td>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Bestätigungsdialog für Meldungen -->
    <v-dialog v-model="reportDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Bestellung melden und stornieren</v-card-title>
        <v-card-text>
          <v-alert type="warning" dense class="mb-3">
            Diese Aktion wird die Bestellung automatisch stornieren. Dies kann nicht rückgängig gemacht werden.
          </v-alert>
          Möchten Sie diese Bestellung wirklich als problematisch melden?
          <v-textarea
            v-model="reportReason"
            label="Grund der Meldung und Stornierung"
            rows="3"
            outlined
            class="mt-4"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="reportDialog = false">Abbrechen</v-btn>
          <v-btn color="error" text @click="confirmReport">Melden & Stornieren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Schließen
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import AppService from "@/services/AppService";
import PublicService from "@/services/PublicService";
import CustomerService from "@/services/CustomerService";
import TrackingService from "@/services/DHL/Tracking";
import OrderService from "@/services/OrderService"; // Import des OrderService für die Meldefunktion

import Header from "@/views/general/header";
import router from "@/plugins/router";
import OrdersHourlyChart from "@/components/charts/OrdersHourlyChart";

export default {
  name: "dashboard",
  components: {
    Header: Header,
    OrdersHourlyChart
  },
  data: () => ({
    loading: true,
    app: {},
    customers: [],
    search: null,
    orders: [],
    hourlyOrdersData: {
      labels: [],
      datasets: [{
        label: 'Bestellungen pro Stunde',
        backgroundColor: '#29b0ce',
        data: []
      }]
    },
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        yAxes: [{
          ticks: {
            beginAtZero: true,
            precision: 0
          }
        }]
      },
      tooltips: {
        callbacks: {
          label: function(tooltipItem) {
            return tooltipItem.yLabel + ' Bestellung(en)';
          }
        }
      }
    },
    selectedDate: new Date().toISOString().substr(0, 10),
    dateMenu: false,
    orderHeaders: [
      { text: "ID", value: "orderId" },
      { text: "PlentyId", value: "plentyId" },
      { text: "Ext. ID", value: "reference" },
      { text: "Datum", value: "date" },
      { text: "Letzte Änderung", value: "updated", sortable: true },
      { text: "Name", value: "name1" },
      { text: "Status", value: "status" },
      { text: "Fehler", value: "hasError" },
      { text: "Zolldokument", value: "invoice" },
      { text: "Versandart", value: "carrier" },
      { text: "Tracking", value: "tracking" },
      { text: "Tracking Status", value: "trackingStatus" },
      { text: "Aktionen", value: "actions", sortable: false, align: "center" },
    ],
    // Neue Variablen für das Melden von Bestellungen
    reportDialog: false,
    selectedOrder: null,
    reportReason: 'Defective Printfile',
    isReporting: {},
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
    filterMode: 'all',
  }),
  computed: {
    filteredOrders() {
      if (this.filterMode === 'errors') {
        return this.orders.filter(order => this.hasOrderItemErrors(order));
      }
      return this.orders;
    },
    selectedDateFormatted() {
      if (!this.selectedDate) return '';
      
      const [year, month, day] = this.selectedDate.split('-');
      return `${day}.${month}.${year}`;
    }
  },
  mounted() {
    this.refresh();
  },
  watch: {
    selectedDate() {
      this.refreshHourlyStats();
    }
  },
  methods: {
    // Neue Methode für die stündlichen Bestellungen
    refreshHourlyStats() {
      if (!this.orders || this.orders.length === 0) return;
      
      // Initialisiere ein Array für 24 Stunden (0-23)
      const hourCounts = Array(24).fill(0);
      const hourLabels = Array(24).fill().map((_, i) => `${i}:00`);
      
      // Ausgewähltes Datum - nur Jahr, Monat, Tag für bessere Vergleichbarkeit
      const selectedDateStr = this.selectedDate;
      console.log('Ausgewähltes Datum:', selectedDateStr);
      
      // Zähler für Debug
      let totalOrdersForSelectedDate = 0;
      
      // Filtere und zähle Bestellungen pro Stunde
      this.orders.forEach(order => {
        try {
          // Bestelldatum in ISO-String umwandeln und auf Tag vergleichen
          const orderDate = new Date(order.date);
          const orderDateStr = orderDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
          
          // Vergleiche nur den Datumsteil (YYYY-MM-DD)
          if (orderDateStr === selectedDateStr) {
            const hour = orderDate.getHours();
            hourCounts[hour]++;
            totalOrdersForSelectedDate++;
          }
        } catch (error) {
          console.error('Fehler beim Verarbeiten des Bestelldatums:', order.date, error);
        }
      });
      
      console.log(`Gefundene Bestellungen für ${selectedDateStr}: ${totalOrdersForSelectedDate}`);
      
      // Wenn keine Daten für das ausgewählte Datum vorhanden sind, zeige Hinweis an
      if (totalOrdersForSelectedDate === 0) {
        // Füge einen leeren Datensatz mit Hinweistext hinzu
        this.hourlyOrdersData = {
          labels: hourLabels,
          datasets: [{
            label: `Keine Bestellungen am ${this.selectedDateFormatted}`,
            backgroundColor: '#e0e0e0',
            data: hourCounts
          }]
        };
      } else {
        // Aktualisiere die Chart-Daten mit gefundenen Bestellungen
        this.hourlyOrdersData = {
          labels: hourLabels,
          datasets: [{
            label: `Bestellungen am ${this.selectedDateFormatted} (${totalOrdersForSelectedDate})`,
            backgroundColor: '#29b0ce',
            data: hourCounts
          }]
        };
      }
    },
    
    // Bestehende Methoden
    getStatusColor(status) {
      switch(status) {
        case 'shipped': return 'green';
        case 'cancled': return 'red';
        case 'pending': return 'orange';
        case 'waiting': return 'blue';
        default: return 'grey';
      }
    },
    
    getStatusText(status) {
      switch(status) {
        case 'shipped': return 'Versandt';
        case 'cancled': return 'Storniert';
        case 'pending': return 'Ausstehend';
        case 'waiting': return 'Wartend';
        default: return status;
      }
    },
    
    getOrderCountByStatus(status) {
      return this.orders ? this.orders.filter(o => o.status === status).length : 0;
    },
    
    getVisibleLines(orderItem) {
      const visibleLines = [];
      for (let i = 1; i <= 10; i++) {
        if (orderItem['line' + i]) {
          visibleLines.push(i);
        }
      }
      return visibleLines;
    },
    
    // Methoden für die Melde-Funktion
    reportOrder(order) {
      this.selectedOrder = order;
      this.reportReason = 'Defective Printfile';
      this.reportDialog = true;
    },
    
    async confirmReport() {
      if (!this.selectedOrder) return;
      
      this.isReporting = { ...this.isReporting, [this.selectedOrder.orderId]: true };
      
      try {
        // Sichere Prüfung der customers-Array ohne optional chaining
        const customerId = this.customers && this.customers.length > 0 ? this.customers[0].id : null;
        
        await OrderService.reportOrder(this.selectedOrder.orderId, {
          reason: this.reportReason,
          orderId: this.selectedOrder.orderId,
          plentyId: this.selectedOrder.plentyId,
          externalId: this.selectedOrder.reference,
          customerId: customerId,
          appId: this.$route.params.id
        });
        
        // Status der Bestellung lokal aktualisieren
        if (this.selectedOrder) {
          this.selectedOrder.status = 'cancled';
          this.$forceUpdate();
        }
        
        this.snackbarText = 'Bestellung erfolgreich gemeldet und storniert';
        this.snackbarColor = 'success';
        this.snackbar = true;
      } catch (error) {
        console.error('Fehler beim Melden der Bestellung:', error);
        this.snackbarText = 'Fehler beim Melden und Stornieren der Bestellung';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.isReporting = { ...this.isReporting, [this.selectedOrder.orderId]: false };
        this.reportDialog = false;
      }
    },

    open(route) {
      router.push("/apps/" + this.app.id + "/" + route);
    },

    edit() {
      router.push("/apps/" + this.app.id + "/edit");
    },

    async doSearch() {
      let customerId = this.customers[0].id;
      let data = (await CustomerService.search(customerId, this.search)).data;
      for(let order of data) {
        if(!this.orders.find(o => o.orderId == order.orderId)) {
          this.orders.push(order);
        }
      }
    },
    
    async refreshTracking(plentyId) {
      try {
        await PublicService.getTrackingNumber(plentyId);
        this.loadOrders();
      } catch (err) {
        throw err;
      }
    },

    async trackingStatus(order) {
      let trackingNr = order.tracking;
      try {
        order.trackingStatus = (
          await TrackingService.track(trackingNr)
        ).data.shipments[0].status.statusCode;
      } catch (e) {
        order.trackingStatus = "unknown";
      }
      this.$forceUpdate();
    },

    async refresh() {
      this.loading = true;
      try {
        let data = (await AppService.getApp(this.$route.params.id)).data;
        if (data) {
          this.app = data;
          await this.loadOrders();
        } else {
          router.replace("/apps");
        }
      } catch (err) {
        if (err.response && err.response.status == 401) {
          router.replace("/auth/login");
        }
      } finally {
        // Loading-State in jedem Fall beenden
        this.loading = false;
      }
    },
    
    async openLink(url) {
      let urls = url.split(",")
      for (let u of urls) {
        setTimeout(() => {
          window.open(u, "_blank");
        }, 500*urls.indexOf(u));
      }
    },

    async loadOrders() {
      let appId = this.$route.params.id;

      try {
        this.customers = (await CustomerService.get(appId)).data;
        
        if (this.customers && this.customers.length > 0) {
          for (let c of this.customers) {
            this.orders = (await CustomerService.getOrders(c.id)).data;
          }
          
          // Nach dem Laden der Bestellungen die Statistik aktualisieren
          this.refreshHourlyStats();
        } else {
          console.log('Keine Kunden gefunden');
        }
      } catch (error) {
        console.error('Fehler beim Laden der Bestellungen:', error);
      }
    },

    // Neue Methoden zur Fehlerprüfung
    hasOrderItemLineData(orderItem) {
      // Prüft, ob ein OrderItem mindestens eine line1-Eigenschaft hat
      return orderItem && (orderItem.line1 !== undefined);
    },
    
    hasOrderItemErrors(order) {
      // Bestellung gilt als fehlerhaft, wenn keine der Positionen ein line1-Attribut hat
      if (!order.orderItems || order.orderItems.length === 0) return false;
      
      // Prüfen, ob mindestens eine Position ein line1-Attribut hat
      const hasAtLeastOneItemWithLine1 = order.orderItems.some(oi => this.hasOrderItemLineData(oi));
      
      // Wenn keine Position line1 hat, ist die Bestellung fehlerhaft
      return !hasAtLeastOneItemWithLine1;
    },
    
    getOrderCountWithErrors() {
      return this.orders ? this.orders.filter(order => this.hasOrderItemErrors(order)).length : 0;
    },
  },
};
</script>

<style scoped>
.max-height-150 {
  max-height: 150px;
  object-fit: contain;
}

.error-border {
  border-left: 4px solid #f44336;
}
</style>

